import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRecentTools } from "@/hooks/use-recent-tools";
import { navGroups } from "@/lib/nav-tree";
import type { NavItem } from "@/lib/nav-items";

export const pathOfNav = (item: NavItem) => item.path.split("?")[0];

/** Shared active test so the sidebar and the mobile drawer agree. */
export const useNavActive = () => {
  const location = useLocation();
  return (item: NavItem) => {
    const [path, query] = item.path.split("?");
    if (location.pathname !== path) return false;
    if (!query) return path !== "/jobs" || !location.search.includes("tool=");
    return location.search.includes(query);
  };
};

const SideNav = () => {
  const navigate = useNavigate();
  const isActive = useNavActive();
  const { trackVisit } = useRecentTools();
  const [collapsed, setCollapsed] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem("tt-sidenav-collapsed") === "1",
  );

  useEffect(() => {
    document.documentElement.style.setProperty("--sidenav-w", collapsed ? "3.5rem" : "15rem");
    localStorage.setItem("tt-sidenav-collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const go = (item: NavItem) => {
    trackVisit(pathOfNav(item), item.label);
    navigate(item.path);
  };

  const Row = ({ item, nested = false }: { item: NavItem; nested?: boolean }) => {
    const active = isActive(item);
    return (
      <button
        onClick={() => go(item)}
        title={collapsed ? item.label : item.desc}
        aria-current={active ? "page" : undefined}
        className={`w-full flex items-center gap-2.5 rounded-md py-2 text-sm font-medium transition-colors ${
          collapsed ? "justify-center px-2" : nested ? "pl-8 pr-3" : "px-3"
        } ${
          active
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-secondary hover:text-foreground"
        }`}
      >
        <item.icon className="w-4 h-4 shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </button>
    );
  };

  return (
    <aside
      className={`hidden md:flex fixed top-0 left-0 bottom-0 z-50 flex-col border-r border-border bg-card transition-[width] ${
        collapsed ? "w-14" : "w-60"
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className={`flex items-center h-12 border-b border-border ${collapsed ? "justify-center px-1" : "px-3"}`}>
        {!collapsed && (
          <span className="text-sm font-bold tracking-tight truncate">Trades Toolbelt</span>
        )}
        <button
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
        {navGroups.map((group, gi) => (
          <div key={group.label || gi} className="space-y-0.5">
            {group.label &&
              (collapsed ? (
                <div className="my-2 h-px bg-border" />
              ) : (
                <div className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                  {group.label}
                </div>
              ))}
            {group.rows.map((row) => (
              <Row key={row.item.path} item={row.item} nested={row.nested} />
            ))}
          </div>
        ))}
      </div>
    </aside>
  );
};

export default SideNav;
